/**
 * UnifiedCodePreview - Componente Consolidado de Preview de Código
 * 
 * Este componente unifica todas las funcionalidades de preview de código de DamyxIA:
 * - ReactPreview (DamyxCodeWorkspace.jsx)
 * - EmbeddedPreview (Dashboard.jsx)
 * - SimplePreview (CommunityGallery.jsx)
 * 
 * Características:
 * - Limpieza automática de código (imports, exports, React.*)
 * - Soporte para React, Recharts, Lucide icons
 * - Corrección automática de sintaxis JSX
 * - Watermark de DamyxIA
 * - Estados de carga y error
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Loader2, AlertCircle, RefreshCw, Maximize2, Minimize2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { reportSilentError } from '../utils/silentErrorReporter';

// =====================================================
// UTILIDADES DE LIMPIEZA DE CÓDIGO
// =====================================================

/**
 * 🟩 iter460 (2026-02-22) · Multi-file CSS fallback.
 *
 * Cuando el LLM emite el módulo como multi-file con archivos .css separados,
 * el preview antes SÓLO leía el .jsx e ignoraba el .css → los cambios de
 * estilo quedaban guardados pero no se aplicaban visualmente (bug del
 * "cambio de color a rojo" reportado por Marcelo 2026-02-22).
 *
 * Esta función extrae todos los archivos .css de `files[]`, los concatena
 * y genera un preámbulo JS que los inyecta en `document.head` como
 * `<style id="damyxia-injected-css">`. Idempotente: en hot-swap actualiza
 * el mismo elemento en vez de duplicar. Sale silencioso si no hay CSS.
 *
 * Retorna el preámbulo JS listo para prependar al JSX o cadena vacía.
 */
const _buildCssInjectionPreamble = (files) => {
  if (!Array.isArray(files) || files.length === 0) return '';
  const cssContents = [];
  for (const f of files) {
    if (!f) continue;
    const name = String(f.name || '');
    const lang = String(f.language || '');
    const isCss = /\.css$/i.test(name) || lang.toLowerCase() === 'css';
    if (!isCss) continue;
    const content = typeof f.content === 'string' ? f.content : '';
    if (content.trim()) cssContents.push(content);
  }
  if (cssContents.length === 0) return '';
  // Serializar como string JS-safe (escape backslashes, backticks y ${}).
  const joined = cssContents.join('\n\n');
  const escaped = joined
    .replace(/\\/g, '\\\\')
    .replace(/`/g, '\\`')
    .replace(/\$\{/g, '\\${');
  return (
    "\n// 🟩 iter460 · CSS injection from multi-file emission\n" +
    "(function(){\n" +
    "  try {\n" +
    "    var _dcssId = 'damyxia-injected-css';\n" +
    "    var _dcss = document.getElementById(_dcssId);\n" +
    "    if (!_dcss) {\n" +
    "      _dcss = document.createElement('style');\n" +
    "      _dcss.id = _dcssId;\n" +
    "      document.head.appendChild(_dcss);\n" +
    "    }\n" +
    "    _dcss.textContent = `" + escaped + "`;\n" +
    "  } catch (_e) { /* silent */ }\n" +
    "})();\n"
  );
};

/**
 * Limpia el código para ejecución en iframe
 * - Elimina imports/exports
 * - Convierte React.useState a useState
 * - Balancea tags JSX y estructuras
 */
export const cleanCodeForPreview = (codeStr) => {
  if (!codeStr) return '';

  let cleaned = codeStr;
  let cssPreamble = ''; // 🟩 iter460 · CSS extraído de files[] para inyectar en iframe.

  // 🟥 P26.60b · Cuando un parser markdown upstream consume las backticks
  // del fence damyxia-action y deja "damyxia-action\n{...}" como primera
  // línea, el JSON se pasa a Babel como código y explota con
  // "Missing semicolon (3:7)". Detectamos ese caso al inicio.
  try {
    const stripLeadingMarker = cleaned.replace(/^\s*damyxia-action\s*\n/i, '');
    if (stripLeadingMarker !== cleaned) {
      cleaned = stripLeadingMarker;
      // Ahora `cleaned` empieza directamente con el JSON: intentamos parsear.
      const trimmed = cleaned.trim();
      if (trimmed.startsWith('{')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && Array.isArray(parsed.files)) {
            const jsFile = parsed.files.find(
              (f) =>
                f &&
                (String(f.language || '').match(/^(jsx?|tsx?|react)$/i) ||
                 String(f.name || '').match(/\.(jsx?|tsx?)$/i))
            );
            // 🟩 iter460 · extraer CSS de files[] (si hay).
            cssPreamble = _buildCssInjectionPreamble(parsed.files);
            if (jsFile && typeof jsFile.content === 'string') {
              cleaned = jsFile.content;
            } else if (typeof parsed.content === 'string' && parsed.content.trim().length > 20) {
              cleaned = parsed.content;
            } else {
              // No pudimos extraer código real; devolvemos vacío para que
              // el iframe muestre estado vacío en vez de intentar compilar.
              return '';
            }
          } else if (typeof parsed.content === 'string' && parsed.content.trim().length > 20) {
            cleaned = parsed.content;
          } else {
            return '';
          }
        } catch (_je) {
          // JSON roto tras strippear el marker: no es código, devolvemos vacío.
          return '';
        }
      }
    }
  } catch (_e) { /* silent */ }

  // 🟩 P26.27 · Si el `code` guardado accidentalmente contiene un fence
  // ```damyxia-action con JSON adentro (ocurre cuando el usuario
  // Revierte y el snapshot previo era el fence crudo, o cuando el
  // Editor Inline recibe pegado un JSON), EXTRAEMOS el `content` del
  // JSON en vez de pasar el JSON entero al iframe (que rompe Babel).
  try {
    const fenceMatch = cleaned.match(/```(?:damyxia-action)?[^\n]*\n([\s\S]*?)```/);
    if (fenceMatch && fenceMatch[1].trim().startsWith('{')) {
      let extracted = false;
      try {
        const parsed = JSON.parse(fenceMatch[1].trim());
        if (parsed && typeof parsed.content === 'string' && parsed.content.trim().length > 20) {
          cleaned = parsed.content;
          extracted = true;
        } else if (Array.isArray(parsed?.files)) {
          const jsFile = parsed.files.find(
            (f) => f && (String(f.language || '').match(/^(jsx?|tsx?|react)$/i) || String(f.name || '').match(/\.(jsx?|tsx?)$/i))
          );
          // 🟩 iter460 · extraer CSS de files[] (si hay).
          cssPreamble = _buildCssInjectionPreamble(parsed.files);
          if (jsFile && typeof jsFile.content === 'string') {
            cleaned = jsFile.content;
            extracted = true;
          }
        }
      } catch (_je) { /* JSON roto: se quita el fence abajo */ }
      if (!extracted) {
        // El fence no se pudo extraer: se ELIMINA para que Babel nunca
        // reciba el bloque damyxia-action crudo (error de consola falso).
        cleaned = cleaned.replace(/```(?:damyxia-action)?[^\n]*\n[\s\S]*?```/g, '').trim();
      }
    } else if (cleaned.trim().startsWith('{') && /"action"\s*:\s*"(create|update)"/.test(cleaned) && /"type"\s*:\s*"(project|module|note|document|task)"/.test(cleaned)) {
      // JSON pelado sin fence — mismo caso. iter450e: agregamos "module"
      // al whitelist para atrapar el bug del owner donde el LLM emitió
      // type:"module" (no soportado por el motor).
      const parsed = JSON.parse(cleaned);
      if (parsed && typeof parsed.content === 'string' && parsed.content.trim().length > 20) {
        cleaned = parsed.content;
      } else if (Array.isArray(parsed?.files)) {
        const jsFile = parsed.files.find(
          (f) => f && (String(f.language || '').match(/^(jsx?|tsx?|react)$/i) || String(f.name || '').match(/\.(jsx?|tsx?)$/i))
        );
        // 🟩 iter460 · extraer CSS de files[] (si hay).
        cssPreamble = _buildCssInjectionPreamble(parsed.files);
        if (jsFile && typeof jsFile.content === 'string') {
          cleaned = jsFile.content;
        } else {
          return '';
        }
      } else {
        return '';
      }
    }
  } catch (_e) {
    // Si el parse falla, seguimos con el código original — el flujo
    // normal ya maneja errores de sintaxis abajo.
  }

  // 🟥 P26.37 · Antes de borrar los imports, detectar los que apuntan a
  // librerías que NO existen en el iframe (react-chartjs-2, chart.js, MUI…).
  // Sin esto sus componentes quedaban indefinidos y la vista previa se veía
  // EN BLANCO sin ningún aviso.
  const SUPPORTED_PKGS = /^(react$|react-dom|recharts|lucide-react|react-router-dom|prop-types|\.{1,2}\/)/;
  const CHART_PKGS = /^(react-chartjs-2|chart\.js)/;
  const stubs = [];
  const importRe = /import\s+([^;]*?)\s+from\s+['"]([^'"]+)['"]/g;
  let impMatch;
  while ((impMatch = importRe.exec(cleaned)) !== null) {
    const clause = impMatch[1].trim();
    const pkg = impMatch[2];
    if (SUPPORTED_PKGS.test(pkg)) continue;
    const names = [];
    const named = clause.match(/\{([^}]*)\}/);
    if (named) {
      named[1].split(',').forEach((part) => {
        const n = part.split(/\s+as\s+/).pop().trim();
        if (n) names.push(n);
      });
    }
    const rest = clause.replace(/\{[^}]*\}/, '').replace(/(^,)|(,$)/g, '').trim();
    if (rest) {
      const dm = rest.match(/\*\s+as\s+([\w$]+)/) || rest.match(/^([\w$]+)/);
      if (dm) names.push(dm[1]);
    }
    names.forEach((n) => stubs.push({ name: n, pkg, isChart: CHART_PKGS.test(pkg) }));
  }

  // Eliminar imports de React
  cleaned = cleaned
    .replace(/import\s+React\s*,?\s*\{[^}]*\}\s*from\s+['"]react['"];?\s*/gi, '')
    .replace(/import\s+\{[^}]*\}\s*from\s+['"]react['"];?\s*/gi, '')
    .replace(/import\s+React\s+from\s+['"]react['"];?\s*/gi, '')
    .replace(/import\s+\*\s+as\s+React\s+from\s+['"]react['"];?\s*/gi, '')
    .replace(/import\s+.*?from\s+['"]react['"];?\s*/gi, '')
    // Eliminar imports de lucide-react
    .replace(/import\s+\{[^}]*\}\s*from\s+['"]lucide-react['"];?\s*/gi, '')
    // Eliminar imports de recharts
    .replace(/import\s+\{[^}]*\}\s*from\s+['"]recharts['"];?\s*/gi, '')
    // Eliminar cualquier otro import
    .replace(/import\s+.*?from\s+['"][^'"]+['"];?\s*/g, '')
    .replace(/import\s+['"][^'"]+['"];?\s*/g, '')
    // Eliminar exports
    .replace(/export\s+default\s+/g, '')
    .replace(/export\s+/g, '');

  // Preámbulo con sustitutos para las librerías ausentes (ver P26.37 arriba).
  if (stubs.length) {
    const preamble = stubs
      .filter((s) => !new RegExp(`(?:function|class|const|let|var)\\s+${s.name}\\b`).test(cleaned))
      .map((s) => (
        `var ${s.name} = (window.__damyx${s.isChart ? 'ChartStub' : 'Fallback'} `
        + `|| function(){ return function(){ return null; }; })`
        + `(${JSON.stringify(s.name)}, ${JSON.stringify(s.pkg)});`
      ))
      .join('\n');
    if (preamble) cleaned = `${preamble}\n${cleaned}`;
  }
  
  // Convertir React.* a funciones directas
  cleaned = cleaned    .replace(/React\.useState/g, 'useState')
    .replace(/React\.useEffect/g, 'useEffect')
    .replace(/React\.useRef/g, 'useRef')
    .replace(/React\.useCallback/g, 'useCallback')
    .replace(/React\.useMemo/g, 'useMemo')
    .replace(/React\.useContext/g, 'useContext')
    .replace(/React\.useReducer/g, 'useReducer')
    .replace(/React\.useLayoutEffect/g, 'useLayoutEffect');

  // Eliminar llamadas a ReactDOM.render(...) / createRoot(...).render(...)
  // que algunos LLMs emiten al final del codigo. Causan doble montaje o
  // conflictos con nuestro mount controlado.
  cleaned = cleaned
    .replace(/ReactDOM\.render\s*\([\s\S]*?\)\s*;?/g, '')
    .replace(/ReactDOM\.createRoot\s*\([^)]*\)\s*\.\s*render\s*\([\s\S]*?\)\s*;?/g, '')
    .replace(/createRoot\s*\([^)]*\)\s*\.\s*render\s*\([\s\S]*?\)\s*;?/g, '');
  
  // Detectar código truncado.
  // 🟥 P26.29 · Un código que termina en `}`, `)`, `;` o `>` está COMPLETO.
  // Antes bastaba con que los últimos 20 chars contuvieran una comilla para
  // marcarlo como truncado (falso positivo en componentes de una línea), lo
  // que activaba las reparaciones heurísticas y rompía el JSX válido.
  const trimmedEnd = cleaned.trim();
  const endsComplete = /[})\];>]$/.test(trimmedEnd);
  const lastChars = cleaned.slice(-20);
  const isTruncated = !endsComplete && lastChars.match(/<\w+\s*$|<\w+\s+\w+=$|<\w+\s+\w+=["'][^"']*$|\w+=$|["'][^"']*$/);

  if (isTruncated) {
    const lines = cleaned.split('\n');
    while (lines.length > 0) {
      const lastLine = lines[lines.length - 1].trim();
      if (lastLine.match(/[>;},)\]]\s*$/) || lastLine === '') break;
      lines.pop();
    }
    cleaned = lines.join('\n');
  }

  // 🟥 P26.29 · Las heurísticas de "balanceo" de tags/brackets destruyen
  // código VÁLIDO: por ejemplo `onChange={e => setV(...)}` contiene un `>`
  // que el regex de self-closing interpreta como cierre de tag, generando
  // `<input value={v} onChange={e= />` → "Adjacent JSX elements".
  // Por eso solo se aplican cuando el código venía TRUNCADO (código
  // incompleto del LLM), nunca sobre código completo.
  if (isTruncated) {
    // Balancear self-closing tags
    const selfClosingTags = ['img', 'br', 'hr', 'input', 'meta', 'link', 'area', 'base', 'col', 'embed', 'param', 'source', 'track', 'wbr'];
    selfClosingTags.forEach(tag => {
      const regex = new RegExp(`<${tag}([^>]*?)(?<!/)>(?!.*</${tag}>)`, 'gi');
      cleaned = cleaned.replace(regex, `<${tag}$1 />`);
    });

    // Balancear tags JSX
    const jsxTags = ['div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'section', 'article', 'header', 'footer', 'nav', 'aside', 'main', 'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'form', 'button', 'a', 'label', 'svg', 'path', 'g', 'rect', 'circle'];
    jsxTags.forEach(tag => {
      const openRegex = new RegExp(`<${tag}(?:\\s[^>]*)?(?<!/)>`, 'gi');
      const closeRegex = new RegExp(`</${tag}>`, 'gi');
      const selfCloseRegex = new RegExp(`<${tag}[^>]*/\\s*>`, 'gi');
      const opens = (cleaned.match(openRegex) || []).length - (cleaned.match(selfCloseRegex) || []).length;
      const closes = (cleaned.match(closeRegex) || []).length;
      if (opens > closes) {
        cleaned += `\n</${tag}>`.repeat(opens - closes);
      }
    });

    // Balancear paréntesis, llaves y corchetes
    let openParens = 0, openBraces = 0, openBrackets = 0;
    let inString = false, stringChar = '';

    for (let i = 0; i < cleaned.length; i++) {
      const char = cleaned[i];
      const prevChar = i > 0 ? cleaned[i - 1] : '';

      if ((char === '"' || char === "'" || char === '`') && prevChar !== '\\') {
        if (!inString) { inString = true; stringChar = char; }
        else if (char === stringChar) { inString = false; }
      }

      if (!inString) {
        if (char === '(') openParens++;
        if (char === ')') openParens--;
        if (char === '{') openBraces++;
        if (char === '}') openBraces--;
        if (char === '[') openBrackets++;
        if (char === ']') openBrackets--;
      }
    }

    if (openBrackets > 0) cleaned += ']'.repeat(openBrackets);
    if (openParens > 0) cleaned += ')'.repeat(openParens);
    if (openBraces > 0) cleaned += '}'.repeat(openBraces);
  }
  
  cleaned = cleaned.trim();
  
  // Asegurar cierre de componente
  if (!cleaned.endsWith(';') && !cleaned.endsWith('}') && !cleaned.endsWith(')')) {
    if (cleaned.includes('const App') || cleaned.includes('function App')) {
      cleaned += '\n};';
    }
  }

  // 🟩 iter460 · Prependar el preámbulo de inyección de CSS multi-file
  // (si el LLM emitió archivos .css en files[]). Idempotente y silente
  // si no hay CSS. Ver `_buildCssInjectionPreamble()`.
  if (cssPreamble) {
    cleaned = cssPreamble + '\n' + cleaned;
  }

  return cleaned;
};

/**
 * 🟥 P26.29 · Detecta si el código es un DOCUMENTO HTML completo.
 * Estos proyectos (render_mode="html", sitios con index.html/style.css/
 * script.js) NO deben pasar por Babel: se inyectan tal cual en el iframe.
 */
export const isFullHtmlDocument = (codeStr) =>
  typeof codeStr === 'string' && /^\s*(<!doctype\s+html|<html[\s>])/i.test(codeStr);

/**
 * Genera el HTML del iframe con todas las dependencias
 */
export const generatePreviewHTML = (code, options = {}) => {
  // Sitio HTML puro → se sirve directo, sin transpilar.
  if (isFullHtmlDocument(code)) return code;
  const { showWatermark = true, theme = 'dark' } = options;
  const cleanedCode = cleanCodeForPreview(code);
  
  const escapedCode = cleanedCode
    .replace(/\\/g, '\\\\')
    .replace(/`/g, '\\`')
    .replace(/\$/g, '\\$');
  
  const bgColor = theme === 'dark' ? '#0f172a' : '#ffffff';
  const textColor = theme === 'dark' ? '#fff' : '#1e293b';
  
  const watermarkHTML = showWatermark ? `
    <div class="damyxia-watermark">
      <img src="/icons/icon-192x192.png" alt="DamyxIA" />
      <span>by DamyxIA</span>
    </div>
  ` : '';
  
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <script src="https://cdn.tailwindcss.com"><\/script>
  <script src="https://unpkg.com/react@18/umd/react.production.min.js"><\/script>
  <script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"><\/script>
  <script src="https://unpkg.com/@babel/standalone@7/babel.min.js"><\/script>
  <script src="https://unpkg.com/prop-types@15/prop-types.min.js"><\/script>
  <script src="https://unpkg.com/recharts@2/umd/Recharts.js"><\/script>
  <script src="https://unpkg.com/lucide@latest"><\/script>
  <style>
    /* Reset agresivo y normalizacion para que cualquier dashboard generado
       se vea correctamente DENTRO del iframe (que NO tiene la altura del
       viewport real, sino la del contenedor exterior). */
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    html, body { height: 100%; width: 100%; overflow-x: hidden; }
    body { font-family: system-ui, -apple-system, sans-serif; background: ${bgColor}; color: ${textColor}; min-height: 100%; }
    #root { min-height: 100%; }
    /* Override: si el dashboard usa min-h-screen (100vh), forzamos que se
       comporte como 100% del iframe para evitar mini-scroll por diferencia
       entre vh real y altura del iframe. */
    .min-h-screen, [class*="min-h-screen"] { min-height: 100% !important; }
    .h-screen, [class*="h-screen"] { height: 100% !important; }
    /* 🟥 LEGIBILIDAD DE CAMPOS — el body del preview usa color claro (tema
       oscuro) y las apps generadas dibujan campos con fondo blanco: el texto
       tipeado quedaba invisible. Estos defaults son a nivel ETIQUETA, así que
       cualquier clase Tailwind del app (text-*/bg-*) los sobreescribe. */
    input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="color"]),
    textarea, select { color: #111827; background-color: #ffffff; caret-color: #111827; }
    input::placeholder, textarea::placeholder { color: #6b7280; opacity: 1; }
    .loading { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; color: #a1a1aa; gap: 12px; }
    .loading-spinner { width: 32px; height: 32px; border: 3px solid #4f46e5; border-top-color: transparent; border-radius: 50%; animation: spin 1s linear infinite; }
    @keyframes spin { to { transform: rotate(360deg); } }
    .error { display: none !important; }
    /* 🟥 CUADRO NEGRO 2026-07-03 — "MOSTRAR SIEMPRE": los errores nunca se
       muestran visualmente en el Área de Trabajo. Se emiten via postMessage
       al parent para un panel opcional. */
    .damyxia-watermark { position: fixed; bottom: 6px; right: 10px; display: flex; align-items: center; gap: 5px; opacity: 0.5; z-index: 9999; }
    .damyxia-watermark img { width: 16px; height: 16px; border-radius: 50%; }
    .damyxia-watermark span { font-size: 9px; background: linear-gradient(90deg, #a78bfa, #6366f1); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
  </style>
</head>
<body>
  <div id="root"><div class="loading"><div class="loading-spinner"></div><span>Compilando componente...</span></div></div>
  ${watermarkHTML}
  
  <script>
    (function() {
      // 🟥 CUADRO NEGRO 2026-07-03 — "MOSTRAR SIEMPRE" helper.
      // Emite errores al parent en lugar de renderizarlos visualmente.
      // El Área de Trabajo NUNCA muestra ventanas rojas.
      function emitSilentError(type, message, extra) {
        try {
          window.__damyxia_errors = window.__damyxia_errors || [];
          var entry = { type: type, message: String(message || ''), timestamp: Date.now() };
          if (extra) entry.extra = extra;
          window.__damyxia_errors.push(entry);
          if (window.parent && window.parent !== window) {
            window.parent.postMessage({
              source: 'damyxia-preview',
              type: 'damyxia:preview:error',
              payload: entry
            }, '*');
          }
        } catch (_) { /* silent */ }
      }
      function clearRoot() {
        // 🟥 P26.60 · Desmontar React ANTES de tocar el DOM para evitar
        // que el próximo re-mount lance "removeChild is not a child".
        try {
          var r = document.getElementById('root');
          if (r && ReactDOM && typeof ReactDOM.unmountComponentAtNode === 'function') {
            try { ReactDOM.unmountComponentAtNode(r); } catch (_) {}
          }
          window.__damyx_last_mount_ok = false;
          if (r) r.innerHTML = '';
        } catch (_) {}
      }

      // 🟥 GUARDIÁN DE LEGIBILIDAD — detecta texto con contraste insuficiente
      // contra su fondo real (ej: blanco heredado sobre tarjeta clara) y lo
      // corrige al negro/blanco que sí se lee. Sólo actúa cuando el contraste
      // es prácticamente nulo (< 2.2), así no altera diseños intencionales.
      function dxColor(c) {
        var m = new RegExp('rgba?\\\\(\\\\s*(\\\\d+)[,\\\\s]+(\\\\d+)[,\\\\s]+(\\\\d+)(?:[,\\\\s\\\\/]+([\\\\d.]+))?').exec(c || '');
        if (!m) return null;
        return { r: +m[1], g: +m[2], b: +m[3], a: m[4] === undefined ? 1 : parseFloat(m[4]) };
      }
      function dxLum(c) {
        function f(v) { v = v / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); }
        return 0.2126 * f(c.r) + 0.7152 * f(c.g) + 0.0722 * f(c.b);
      }
      function dxContrast(a, b) {
        var l1 = dxLum(a), l2 = dxLum(b);
        var hi = Math.max(l1, l2), lo = Math.min(l1, l2);
        return (hi + 0.05) / (lo + 0.05);
      }
      function dxEffectiveBg(el) {
        var node = el;
        while (node && node.nodeType === 1) {
          var c = dxColor(window.getComputedStyle(node).backgroundColor);
          if (c && c.a > 0.15) return c;
          node = node.parentElement;
        }
        var b = dxColor(window.getComputedStyle(document.body).backgroundColor);
        return b && b.a > 0.15 ? b : { r: 15, g: 23, b: 42, a: 1 };
      }
      function dxHasOwnText(el) {
        for (var j = 0; j < el.childNodes.length; j++) {
          var n = el.childNodes[j];
          if (n.nodeType === 3 && n.nodeValue && n.nodeValue.trim()) return true;
        }
        return false;
      }
      function fixReadability() {
        try {
          var root = document.getElementById('root');
          if (!root) return;
          var els = root.querySelectorAll('*');
          for (var i = 0; i < els.length; i++) {
            var el = els[i];
            var tag = el.tagName;
            if (tag === 'SCRIPT' || tag === 'STYLE' || tag === 'SVG' || tag === 'PATH') continue;
            var isField = (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT');
            if (!isField && !dxHasOwnText(el)) continue;
            var cs = window.getComputedStyle(el);
            var fg = dxColor(cs.color);
            if (!fg) continue;
            var bg;
            if (isField) {
              var own = dxColor(cs.backgroundColor);
              bg = (own && own.a > 0.15) ? own : dxEffectiveBg(el.parentElement || el);
            } else {
              bg = dxEffectiveBg(el);
            }
            var invisible = (fg.a < 0.2) || (dxContrast(fg, bg) < 2.2);
            if (!invisible) continue;
            var readable = dxLum(bg) > 0.4 ? '#111827' : '#f8fafc';
            el.style.setProperty('color', readable, 'important');
            if (isField) el.style.setProperty('caret-color', readable, 'important');
          }
        } catch (_) { /* silent */ }
      }
      function scheduleReadabilityGuard() {
        [60, 300, 900].forEach(function(d) { setTimeout(fixReadability, d); });
        if (window.__dxReadabilityObserver) return;
        try {
          var pending = null;
          var obs = new MutationObserver(function() {
            if (pending) clearTimeout(pending);
            pending = setTimeout(fixReadability, 250);
          });
          obs.observe(document.getElementById('root'), { childList: true, subtree: true, characterData: true });
          window.__dxReadabilityObserver = obs;
        } catch (_) { /* silent */ }
      }
      var attempts = 0;
      var maxAttempts = 100;
      
      function checkLibraries() {
        attempts++;
        if (typeof React !== 'undefined' && typeof ReactDOM !== 'undefined' && typeof Babel !== 'undefined') {
          setTimeout(renderComponent, 100);
        } else if (attempts < maxAttempts) {
          setTimeout(checkLibraries, 100);
        } else {
          clearRoot();
          emitSilentError('libs', 'Las bibliotecas no se cargaron correctamente.');
        }
      }
      
      function renderComponent() {
        // 🟥 P26.37 · TOLERANCIA A LIBRERÍAS NO DISPONIBLES
        // Los proyectos generados a veces importan paquetes que no existen en
        // el iframe (react-chartjs-2, chart.js, etc.). Antes eso dejaba la
        // vista previa EN BLANCO. Ahora se sustituyen por sustitutos visibles.
        window.__damyxChartStub = function(name) {
          return function(props) {
            var data = (props && props.data) || {};
            var labels = data.labels || [];
            var ds = (data.datasets && data.datasets[0]) || {};
            var vals = ds.data || [];
            var max = Math.max.apply(null, vals.concat([1]));
            return React.createElement('div', { style: { padding: '12px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px' } },
              React.createElement('div', { style: { fontSize: '12px', color: '#64748b', marginBottom: '8px' } }, ds.label || name),
              labels.map(function(l, i) {
                return React.createElement('div', { key: i, style: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' } },
                  React.createElement('span', { style: { width: '96px', fontSize: '12px', color: '#334155' } }, String(l)),
                  React.createElement('div', { style: { flex: 1, background: '#eef2f7', borderRadius: '4px', height: '14px' } },
                    React.createElement('div', { style: { width: ((vals[i] || 0) / max * 100) + '%', background: '#1e3a8a', height: '100%', borderRadius: '4px' } })),
                  React.createElement('span', { style: { width: '44px', textAlign: 'right', fontSize: '12px', color: '#0f172a' } }, String(vals[i] || 0)));
              }));
          };
        };
        window.__damyxFallback = function(name, pkg) {
          return function(props) {
            return React.createElement('div', {
              style: { padding: '10px 12px', border: '1px dashed #94a3b8', borderRadius: '8px', color: '#475569', background: '#f8fafc', fontSize: '12px' }
            }, name + ' · la librería "' + pkg + '" no está disponible en la vista previa', props && props.children);
          };
        };

        // Barrera de errores: un fallo de render ya NO deja la pantalla en blanco.
        var DamyxBoundary = (function() {
          function B(props) { React.Component.call(this, props); this.state = { err: null }; }
          B.prototype = Object.create(React.Component.prototype);
          B.prototype.constructor = B;
          B.getDerivedStateFromError = function(err) { return { err: err }; };
          B.prototype.componentDidCatch = function(err) { console.error('[DamyxIA preview] render error:', err); };
          B.prototype.render = function() {
            if (this.state.err) {
              return React.createElement('div', { style: { padding: '20px', fontFamily: 'monospace', color: '#b91c1c', background: '#fef2f2' } },
                React.createElement('div', { style: { fontWeight: 'bold', marginBottom: '8px' } }, 'Error al renderizar la app'),
                React.createElement('div', null, String(this.state.err && (this.state.err.message || this.state.err))));
            }
            return this.props.children;
          };
          return B;
        })();
        window.__damyxBoundary = DamyxBoundary;

        var useState = React.useState;
        var useEffect = React.useEffect;
        var useRef = React.useRef;
        var useCallback = React.useCallback;
        var useMemo = React.useMemo;
        
        // Recharts
        var Recharts = window.Recharts || {};
        var LineChart = Recharts.LineChart;
        var Line = Recharts.Line;
        var AreaChart = Recharts.AreaChart;
        var Area = Recharts.Area;
        var BarChart = Recharts.BarChart;
        var Bar = Recharts.Bar;
        var PieChart = Recharts.PieChart;
        var Pie = Recharts.Pie;
        var Cell = Recharts.Cell;
        var XAxis = Recharts.XAxis;
        var YAxis = Recharts.YAxis;
        var CartesianGrid = Recharts.CartesianGrid;
        var Tooltip = Recharts.Tooltip;
        var Legend = Recharts.Legend;
        var ResponsiveContainer = Recharts.ResponsiveContainer;
        
        // Lucide icons helper
        function createIcon(name) {
          return function(props) {
            var ref = useRef(null);
            useEffect(function() {
              if (ref.current && window.lucide) {
                ref.current.innerHTML = '';
                var icon = window.lucide.icons[name];
                if (icon) {
                  var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                  svg.setAttribute('width', props.size || 24);
                  svg.setAttribute('height', props.size || 24);
                  svg.setAttribute('viewBox', '0 0 24 24');
                  svg.setAttribute('fill', 'none');
                  svg.setAttribute('stroke', 'currentColor');
                  svg.setAttribute('stroke-width', '2');
                  svg.setAttribute('stroke-linecap', 'round');
                  svg.setAttribute('stroke-linejoin', 'round');
                  svg.innerHTML = icon[2].join('');
                  if (props.className) svg.setAttribute('class', props.className);
                  ref.current.appendChild(svg);
                }
              }
            }, []);
            return React.createElement('span', { ref: ref, className: props.className });
          };
        }
        
        // Common Lucide icons
        var Plus = createIcon('plus');
        var Minus = createIcon('minus');
        var Check = createIcon('check');
        var X = createIcon('x');
        var Search = createIcon('search');
        var Settings = createIcon('settings');
        var User = createIcon('user');
        var Mail = createIcon('mail');
        var Phone = createIcon('phone');
        var Calendar = createIcon('calendar');
        var Clock = createIcon('clock');
        var Star = createIcon('star');
        var Heart = createIcon('heart');
        var Home = createIcon('home');
        var Edit = createIcon('edit');
        var Edit2 = createIcon('edit-2');
        var Edit3 = createIcon('edit-3');
        var Pencil = createIcon('pencil');
        var PenTool = createIcon('pen-tool');
        var Trash = createIcon('trash');
        var Trash2 = createIcon('trash-2');
        var Save = createIcon('save');
        var Download = createIcon('download');
        var Upload = createIcon('upload');
        var Copy = createIcon('copy');
        var Share = createIcon('share');
        var Link = createIcon('link');
        var ExternalLink = createIcon('external-link');
        var Menu = createIcon('menu');
        var MoreVertical = createIcon('more-vertical');
        var MoreHorizontal = createIcon('more-horizontal');
        var ChevronLeft = createIcon('chevron-left');
        var ChevronRight = createIcon('chevron-right');
        var ChevronUp = createIcon('chevron-up');
        var ChevronDown = createIcon('chevron-down');
        var ArrowLeft = createIcon('arrow-left');
        var ArrowRight = createIcon('arrow-right');
        var ArrowUp = createIcon('arrow-up');
        var ArrowDown = createIcon('arrow-down');
        var RefreshCw = createIcon('refresh-cw');
        var Loader = createIcon('loader');
        var AlertCircle = createIcon('alert-circle');
        var Info = createIcon('info');
        var HelpCircle = createIcon('help-circle');
        var Bell = createIcon('bell');
        var Filter = createIcon('filter');
        var Sliders = createIcon('sliders');
        var Grid = createIcon('grid');
        var List = createIcon('list');
        var Eye = createIcon('eye');
        var EyeOff = createIcon('eye-off');
        var Lock = createIcon('lock');
        var Unlock = createIcon('unlock');
        var Shield = createIcon('shield');
        var Key = createIcon('key');
        var LogIn = createIcon('log-in');
        var LogOut = createIcon('log-out');
        var UserPlus = createIcon('user-plus');
        var Users = createIcon('users');
        var Folder = createIcon('folder');
        var File = createIcon('file');
        var FileText = createIcon('file-text');
        var Image = createIcon('image');
        var Video = createIcon('video');
        var Music = createIcon('music');
        var Camera = createIcon('camera');
        var Mic = createIcon('mic');
        var Play = createIcon('play');
        var Pause = createIcon('pause');
        var Square = createIcon('square');
        var Circle = createIcon('circle');
        var Sun = createIcon('sun');
        var Moon = createIcon('moon');
        var Cloud = createIcon('cloud');
        var Zap = createIcon('zap');
        var TrendingUp = createIcon('trending-up');
        var TrendingDown = createIcon('trending-down');
        var BarChart2 = createIcon('bar-chart-2');
        var PieChart2 = createIcon('pie-chart');
        var Activity = createIcon('activity');
        var DollarSign = createIcon('dollar-sign');
        var CreditCard = createIcon('credit-card');
        var ShoppingCart = createIcon('shopping-cart');
        var Package = createIcon('package');
        var Truck = createIcon('truck');
        var MapPin = createIcon('map-pin');
        var Navigation = createIcon('navigation');
        var Globe = createIcon('globe');
        var Wifi = createIcon('wifi');
        var Bluetooth = createIcon('bluetooth');
        var Battery = createIcon('battery');
        var Cpu = createIcon('cpu');
        var Monitor = createIcon('monitor');
        var Smartphone = createIcon('smartphone');
        var Tablet = createIcon('tablet');
        var Laptop = createIcon('laptop');
        var GripVertical = createIcon('grip-vertical');
        
        // Additional common icons
        var Sparkles = createIcon('sparkles');
        var Tag = createIcon('tag');
        var Tags = createIcon('tags');
        var Bookmark = createIcon('bookmark');
        var Award = createIcon('award');
        var Gift = createIcon('gift');
        var Percent = createIcon('percent');
        var Hash = createIcon('hash');
        var AtSign = createIcon('at-sign');
        var Inbox = createIcon('inbox');
        var Send = createIcon('send');
        var MessageCircle = createIcon('message-circle');
        var MessageSquare = createIcon('message-square');
        var ThumbsUp = createIcon('thumbs-up');
        var ThumbsDown = createIcon('thumbs-down');
        var Smile = createIcon('smile');
        var Frown = createIcon('frown');
        var Meh = createIcon('meh');
        var AlertTriangle = createIcon('alert-triangle');
        var CheckCircle = createIcon('check-circle');
        var CheckCircle2 = createIcon('check-circle-2');
        var XCircle = createIcon('x-circle');
        var MinusCircle = createIcon('minus-circle');
        var PlusCircle = createIcon('plus-circle');
        var Layers = createIcon('layers');
        var Layout = createIcon('layout');
        var LayoutGrid = createIcon('layout-grid');
        var LayoutList = createIcon('layout-list');
        var Columns = createIcon('columns');
        var Rows = createIcon('rows');
        var Sidebar = createIcon('sidebar');
        var PanelLeft = createIcon('panel-left');
        var PanelRight = createIcon('panel-right');
        var ArrowUpRight = createIcon('arrow-up-right');
        var ArrowDownRight = createIcon('arrow-down-right');
        var ArrowUpLeft = createIcon('arrow-up-left');
        var ArrowDownLeft = createIcon('arrow-down-left');
        var RotateCw = createIcon('rotate-cw');
        var RotateCcw = createIcon('rotate-ccw');
        var Undo = createIcon('undo');
        var Redo = createIcon('redo');
        var Clipboard = createIcon('clipboard');
        var ClipboardCheck = createIcon('clipboard-check');
        var ClipboardList = createIcon('clipboard-list');
        var FileCheck = createIcon('file-check');
        var FilePlus = createIcon('file-plus');
        var FolderPlus = createIcon('folder-plus');
        var FolderOpen = createIcon('folder-open');

        // 🟥 CUADRO NEGRO 2026-07-01 — SHIM DEFENSIVO Router en iframe.
        // El iframe NO carga react-router-dom. Si Genio (Lyon) genera código
        // que llama useHistory/useNavigate/Link/Routes/BrowserRouter, todo
        // crasheaba con "X is not a function". Estos shims exponen APIs
        // compatibles v5+v6 que hacen postMessage al parent (DamyxIA app)
        // para que la app real decida si navegar o no. No-op seguro cuando
        // el módulo no usa navegación.
        var __damyxiaNav = function(path, opts, kind) {
          try {
            window.parent.postMessage({
              type: 'damyxia-navigate',
              kind: kind || 'push',
              path: (typeof path === 'string' ? path : null),
              delta: (typeof path === 'number' ? path : null),
              opts: opts || null,
              source: 'iframe-preview'
            }, '*');
          } catch (e) { /* iframe cross-origin: no-op */ }
        };
        // v5 hook
        var useHistory = function() {
          return {
            push: function(path, state) { __damyxiaNav(path, { state: state }, 'push'); },
            replace: function(path, state) { __damyxiaNav(path, { state: state }, 'replace'); },
            goBack: function() { __damyxiaNav(null, null, 'back'); },
            goForward: function() { __damyxiaNav(null, null, 'forward'); },
            go: function(n) { __damyxiaNav(n, null, 'go'); },
            block: function() { return function(){}; },
            listen: function() { return function(){}; },
            location: { pathname: '/', search: '', hash: '', state: null, key: 'preview' }
          };
        };
        // v6 hook
        var useNavigate = function() {
          return function(pathOrDelta, opts) {
            if (typeof pathOrDelta === 'number') __damyxiaNav(pathOrDelta, null, 'go');
            else __damyxiaNav(pathOrDelta, opts, (opts && opts.replace) ? 'replace' : 'push');
          };
        };
        // Location / params
        var useLocation = function() {
          return { pathname: '/', search: '', hash: '', state: null, key: 'preview' };
        };
        var useParams = function() { return {}; };
        var useSearchParams = function() {
          return [new URLSearchParams(''), function(){ /* no-op */ }];
        };
        var useMatch = function() { return null; };
        var useRouteMatch = function() { return { path: '/', url: '/', isExact: true, params: {} }; };
        var useOutlet = function() { return null; };
        var useOutletContext = function() { return null; };
        // Link component (v5 + v6 compatible). 🟥 SMART FALLBACK — si recibe
        // props.to o props.href funciona como router link; si no, se comporta
        // como el ícono Lucide "link" (compat con módulos previos al shim).
        var _LinkIcon = createIcon('link');
        var Link = function(props) {
          if (props && (props.to != null || props.href != null)) {
            var to = props.to || props.href || '#';
            return React.createElement('a', {
              href: (typeof to === 'string' ? to : '#'),
              onClick: function(e) {
                if (e && e.preventDefault) e.preventDefault();
                __damyxiaNav(to, null, props.replace ? 'replace' : 'push');
                if (typeof props.onClick === 'function') props.onClick(e);
              },
              className: props.className,
              style: props.style,
              target: props.target,
              rel: props.rel,
              'aria-label': props['aria-label']
            }, props.children);
          }
          // Fallback: ícono Lucide link.
          return React.createElement(_LinkIcon, props);
        };
        var NavLink = Link;
        // Router primitives — wrappers no-op que solo renderizan children.
        var BrowserRouter = function(props) { return props.children || null; };
        var HashRouter = BrowserRouter;
        var MemoryRouter = BrowserRouter;
        var StaticRouter = BrowserRouter;
        var Router = BrowserRouter;
        var Routes = BrowserRouter;
        var Switch = BrowserRouter; // v5
        var Route = function(props) {
          // v6: <Route element={<X/>} />, v5: <Route component={X}/> o children.
          if (props.element) return props.element;
          if (props.component) return React.createElement(props.component);
          if (props.render && typeof props.render === 'function') return props.render({ location: useLocation() });
          return props.children || null;
        };
        var Outlet = function() { return null; };
        var Navigate = function(props) {
          // v6 Navigate — dispara postMessage al montarse.
          useEffect(function() {
            if (props && props.to) __damyxiaNav(props.to, null, props.replace ? 'replace' : 'push');
          }, []);
          return null;
        };
        var Redirect = Navigate; // v5 alias

        try {
          // 🟩 P26.22 · Hot-swap del código sin recargar el iframe.
          // window.__damyx_current_code guarda el código activo. Se
          // actualiza inicialmente desde el srcDoc, y luego el listener
          // 'message' abajo lo remplaza cuando llega un update via
          // postMessage → llamamos renderComponent() de nuevo.
          var userCode = window.__damyx_current_code || \`${escapedCode}\`;

          if (!userCode || !userCode.trim()) {
            clearRoot(); emitSilentError('empty', 'Sin código para previsualizar.');
            return;
          }

          // 1. Transpilar JSX -> JS plano con Babel.
          // 🟩 iter459 (2026-02-22) · Auto-dedupe silencioso de top-level.
          // ANTES pre-check iter457 hacía return al detectar doble declaración,
          // dejando el Workspace en blanco. Ahora conservamos SÓLO la última
          // declaración de cada identificador Capital a inicio de línea
          // (columna 0), que es la versión con el cambio nuevo del LLM. Sin
          // banner, sin bloqueo, sin blank workspace. Cuadro Negro puro.
          try {
            var _decLineRe = /^(?:export\\s+default\\s+)?(?:async\\s+)?(?:function|class|const|let|var)\\s+([A-Z][A-Za-z0-9_$]*)/;
            var _codeLines = String(userCode).split('\\n');
            var _segments = [];
            var _curName = '';
            var _curBody = [];
            for (var _li = 0; _li < _codeLines.length; _li++) {
              var _line = _codeLines[_li];
              var _mm = _decLineRe.exec(_line);
              if (_mm) {
                if (_curBody.length) _segments.push({ name: _curName, body: _curBody.join('\\n') });
                _curName = _mm[1];
                _curBody = [_line];
              } else {
                _curBody.push(_line);
              }
            }
            if (_curBody.length) _segments.push({ name: _curName, body: _curBody.join('\\n') });
            // Contar duplicados.
            var _counts = Object.create(null);
            for (var _sc = 0; _sc < _segments.length; _sc++) {
              var _sn = _segments[_sc].name;
              if (_sn) _counts[_sn] = (_counts[_sn] || 0) + 1;
            }
            var _dupNames = [];
            for (var _kd in _counts) if (_counts[_kd] > 1) _dupNames.push(_kd);
            if (_dupNames.length > 0) {
              // Recorrer en reverso, marcar el primer visto (=último en orden) como keep.
              var _seenLast = Object.create(null);
              for (var _sr = _segments.length - 1; _sr >= 0; _sr--) {
                var _srn = _segments[_sr].name;
                if (_srn && _counts[_srn] > 1) {
                  if (_seenLast[_srn]) _segments[_sr].drop = true;
                  else _seenLast[_srn] = true;
                }
              }
              var _kept = [];
              for (var _sk = 0; _sk < _segments.length; _sk++) {
                if (!_segments[_sk].drop) _kept.push(_segments[_sk].body);
              }
              userCode = _kept.join('\\n');
              try { window.__damyx_current_code = userCode; } catch (_) {}
              console.warn('[DamyxIA preview] iter459 auto-dedupe:', _dupNames);
              // NO llamamos emitSilentError: el fix es silencioso, no un error.
            }
          } catch (_dupErr) { /* si el dedupe falla, dejamos que Babel se encargue */ }

          var transpiledCode;
          try {
            transpiledCode = Babel.transform(userCode, { presets: ['react'] }).code;
          } catch (babelErr) {
            console.error('[DamyxIA preview] Babel error:', babelErr);
            clearRoot(); emitSilentError('babel', (babelErr.message || babelErr));
            return;
          }

          // 2. Detectar nombres de componentes declarados (mayuscula inicial).
          var componentNames = [];
          var declRegex = /(?:^|[\\n;{])\\s*(?:var|let|const|function|class)\\s+([A-Z]\\w*)/g;
          var declMatch;
          while ((declMatch = declRegex.exec(transpiledCode)) !== null) {
            if (componentNames.indexOf(declMatch[1]) < 0) componentNames.push(declMatch[1]);
          }

          // Babel a veces convierte 'class X' en 'var X = function _X(){...}',
          // que ya queda capturado por el regex anterior gracias a 'var'.

          // 3. Ordenar candidatos: App / Default / Main / Root primero,
          //    luego el ULTIMO declarado (suele ser el componente raiz).
          var preferred = ['App', 'Default', 'Main', 'Root', 'Dashboard', 'Component', 'Page'];
          var ordered = preferred.filter(function(n){ return componentNames.indexOf(n) >= 0; });
          for (var ci = componentNames.length - 1; ci >= 0; ci--) {
            if (preferred.indexOf(componentNames[ci]) < 0) ordered.push(componentNames[ci]);
          }
          if (ordered.length === 0) {
            clearRoot(); emitSilentError('no_component', 'No se encontró declaración de componente', { snippet: (transpiledCode.slice(0, 400) || '(vacío)') });
            return;
          }

          console.log('[DamyxIA preview] Componentes detectados:', componentNames, 'orden:', ordered);

          // 4. Inyeccion de dependencias via new Function (bulletproof scope).
          //    Construimos una funcion que recibe TODAS las dependencias como
          //    parametros y devuelve el primer componente que sea function/class.
          var depNames = [
            'React', 'ReactDOM',
            'useState', 'useEffect', 'useRef', 'useCallback', 'useMemo', 'useContext', 'useReducer', 'useLayoutEffect',
            // Recharts
            'LineChart', 'Line', 'AreaChart', 'Area', 'BarChart', 'Bar', 'PieChart', 'Pie', 'Cell',
            'XAxis', 'YAxis', 'CartesianGrid', 'Tooltip', 'Legend', 'ResponsiveContainer', 'Recharts',
            // Lucide icons (todos los que ya teniamos definidos arriba)
            'Plus','Minus','Check','X','Search','Settings','User','Mail','Phone','Calendar','Clock','Star','Heart','Home',
            'Edit','Edit2','Edit3','Pencil','PenTool','Trash','Trash2','Save','Download','Upload','Copy','Share','ExternalLink',
            'Menu','MoreVertical','MoreHorizontal','ChevronLeft','ChevronRight','ChevronUp','ChevronDown',
            'ArrowLeft','ArrowRight','ArrowUp','ArrowDown','RefreshCw','Loader','AlertCircle','Info','HelpCircle','Bell','Filter','Sliders',
            'Grid','List','Eye','EyeOff','Lock','Unlock','Shield','Key','LogIn','LogOut','UserPlus','Users',
            'Folder','File','FileText','Image','Video','Music','Camera','Mic','Play','Pause','Square','Circle',
            'Sun','Moon','Cloud','Zap','TrendingUp','TrendingDown','BarChart2','PieChart2','Activity','DollarSign','CreditCard','ShoppingCart','Package','Truck',
            'MapPin','Navigation','Globe','Wifi','Bluetooth','Battery','Cpu','Monitor','Smartphone','Tablet','Laptop','GripVertical',
            'Sparkles','Tag','Tags','Bookmark','Award','Gift','Percent','Hash','AtSign','Inbox','Send','MessageCircle','MessageSquare',
            'ThumbsUp','ThumbsDown','Smile','Frown','Meh','AlertTriangle','CheckCircle','CheckCircle2','XCircle','MinusCircle','PlusCircle',
            'Layers','Layout','LayoutGrid','LayoutList','Columns','Rows','Sidebar','PanelLeft','PanelRight',
            'ArrowUpRight','ArrowDownRight','ArrowUpLeft','ArrowDownLeft','RotateCw','RotateCcw','Undo','Redo',
            'Clipboard','ClipboardCheck','ClipboardList','FileCheck','FilePlus','FolderPlus','FolderOpen',
            // 🟥 SHIM Router — funciones no-op seguras (v5 + v6 API).
            'useHistory','useNavigate','useLocation','useParams','useSearchParams','useMatch','useRouteMatch','useOutlet','useOutletContext',
            'Link','NavLink','BrowserRouter','HashRouter','MemoryRouter','StaticRouter','Router','Routes','Switch','Route','Outlet','Navigate','Redirect'
          ];
          var depValues = [
            React, ReactDOM,
            useState, useEffect, useRef, useCallback, useMemo, React.useContext, React.useReducer, React.useLayoutEffect,
            LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
            XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Recharts,
            Plus, Minus, Check, X, Search, Settings, User, Mail, Phone, Calendar, Clock, Star, Heart, Home,
            Edit, Edit2, Edit3, Pencil, PenTool, Trash, Trash2, Save, Download, Upload, Copy, Share, ExternalLink,
            Menu, MoreVertical, MoreHorizontal, ChevronLeft, ChevronRight, ChevronUp, ChevronDown,
            ArrowLeft, ArrowRight, ArrowUp, ArrowDown, RefreshCw, Loader, AlertCircle, Info, HelpCircle, Bell, Filter, Sliders,
            Grid, List, Eye, EyeOff, Lock, Unlock, Shield, Key, LogIn, LogOut, UserPlus, Users,
            Folder, File, FileText, Image, Video, Music, Camera, Mic, Play, Pause, Square, Circle,
            Sun, Moon, Cloud, Zap, TrendingUp, TrendingDown, BarChart2, PieChart2, Activity, DollarSign, CreditCard, ShoppingCart, Package, Truck,
            MapPin, Navigation, Globe, Wifi, Bluetooth, Battery, Cpu, Monitor, Smartphone, Tablet, Laptop, GripVertical,
            Sparkles, Tag, Tags, Bookmark, Award, Gift, Percent, Hash, AtSign, Inbox, Send, MessageCircle, MessageSquare,
            ThumbsUp, ThumbsDown, Smile, Frown, Meh, AlertTriangle, CheckCircle, CheckCircle2, XCircle, MinusCircle, PlusCircle,
            Layers, Layout, LayoutGrid, LayoutList, Columns, Rows, Sidebar, PanelLeft, PanelRight,
            ArrowUpRight, ArrowDownRight, ArrowUpLeft, ArrowDownLeft, RotateCw, RotateCcw, Undo, Redo,
            Clipboard, ClipboardCheck, ClipboardList, FileCheck, FilePlus, FolderPlus, FolderOpen,
            // 🟥 SHIM Router values (same order as depNames).
            useHistory, useNavigate, useLocation, useParams, useSearchParams, useMatch, useRouteMatch, useOutlet, useOutletContext,
            Link, NavLink, BrowserRouter, HashRouter, MemoryRouter, StaticRouter, Router, Routes, Switch, Route, Outlet, Navigate, Redirect
          ];

          // Construimos una expresion booleana que devuelve el primer componente valido.
          var probeExpr = ordered.map(function(n){
            return '(typeof ' + n + ' !== "undefined" && (typeof ' + n + ' === "function" || (typeof ' + n + ' === "object" && ' + n + ' !== null)) ? ' + n + ' : null)';
          }).join(' || ');

          var fnBody = transpiledCode + '\\n;return ' + probeExpr + ';';

          var AppComponent;
          try {
            var factory = Function.apply(null, depNames.concat([fnBody]));
            AppComponent = factory.apply(null, depValues);
          } catch (runErr) {
            console.error('[DamyxIA preview] Runtime error:', runErr);
            clearRoot(); emitSilentError('runtime', (runErr.message || runErr), { detected: componentNames.join(', ') });
            return;
          }

          // 5. Render.
          if (AppComponent && (typeof AppComponent === 'function' || (typeof AppComponent === 'object' && AppComponent.$$typeof))) {
            var rootEl = document.getElementById('root');
            // 🟥 P26.60 · FIX removeChild al cambiar de módulo.
            // Cuando cambia radicalmente el componente raíz (ej. Contador →
            // Bienvenida) y re-renderizamos sobre el mismo root, React 17
            // puede fallar la reconciliación con "removeChild is not a child".
            // Desmontamos EXPLÍCITAMENTE antes de re-renderizar.
            var _prevMounted = !!(window.__damyx_last_mount_ok);
            if (_prevMounted && rootEl && ReactDOM && typeof ReactDOM.unmountComponentAtNode === 'function') {
              try { ReactDOM.unmountComponentAtNode(rootEl); } catch (_umErr) { /* silent */ }
            }
            var _tryRender = function () {
              return ReactDOM.render(
                React.createElement(window.__damyxBoundary, null, React.createElement(AppComponent)),
                rootEl
              );
            };
            try {
              _tryRender();
              window.__damyx_last_mount_ok = true;
              console.log('[DamyxIA preview] OK render');
              try {
                parent.postMessage({ source: 'damyxia-preview', type: 'damyxia:preview:rendered' }, '*');
              } catch (e) { /* silent */ }
              scheduleReadabilityGuard();
            } catch (renderErr) {
              // 🟥 P26.60 · Recovery: si el render sigue fallando (raro tras
              // el unmount), limpiamos el DOM del root manualmente y
              // reintentamos. Solo aquí es seguro tocar innerHTML porque
              // NO hay un tree de React montado.
              try {
                if (rootEl) rootEl.innerHTML = '';
                _tryRender();
                window.__damyx_last_mount_ok = true;
                console.log('[DamyxIA preview] OK render (post-recovery)');
                try {
                  parent.postMessage({ source: 'damyxia-preview', type: 'damyxia:preview:rendered' }, '*');
                } catch (e) { /* silent */ }
                scheduleReadabilityGuard();
              } catch (renderErr2) {
                window.__damyx_last_mount_ok = false;
                console.error('[DamyxIA preview] Render error:', renderErr2);
                clearRoot(); emitSilentError('render', (renderErr2.message || renderErr2));
              }
            }
          } else {
            clearRoot(); emitSilentError('no_valid_component', 'No se encontró componente válido para montar', { detected: (componentNames.join(', ') || 'ninguno') });
          }
        } catch (err) {
          console.error('[DamyxIA preview] Fatal:', err);
          clearRoot(); emitSilentError('fatal', (err.message || err));
        }
      }
      
      window.addEventListener('message', function (ev) {
        if (ev && ev.data && ev.data.type === 'DAMYX_STOP_COMPILE') {
          var r = document.getElementById('root');
          if (r && r.querySelector('.loading')) {
            r.innerHTML = '<div class="loading"><span>Compilación detenida. Usá Reintentar.</span></div>';
          }
        }
      });

      checkLibraries();

      // 🟥 P26.40 · PUENTE DE DEPLOY: el botón "Deploy" que la app generada
      // dibuja ya no es de adorno. Se expone window.damyxiaDeploy() y, además,
      // cualquier click en un botón cuyo texto sea Deploy/Publicar avisa al
      // Workspace para abrir la publicación REAL.
      window.damyxiaDeploy = function () {
        try { parent.postMessage({ type: 'DAMYX_DEPLOY_REQUEST' }, '*'); } catch (e) { /* silent */ }
      };
      document.addEventListener('click', function (ev) {
        try {
          var node = ev.target;
          var el = node && node.closest ? node.closest('button, a, [role="button"]') : null;
          if (!el) return;
          var txt = (el.innerText || el.textContent || '').trim().toLowerCase();
          if (!txt || txt.length > 28) return;
          if (/\\b(deploy|desplegar|publicar|publish)\\b/.test(txt)) window.damyxiaDeploy();
        } catch (e) { /* silent */ }
      }, true);

      // 🟩 P26.22 · Listener postMessage para hot-swap del código.
      // El parent (UnifiedCodePreview) envía {type:'DAMYX_UPDATE_CODE', code:...}
      // cuando el módulo se actualiza desde el chat. Actualizamos la
      // variable global y disparamos un nuevo render SIN recargar el iframe
      // — así el usuario no ve el flash "desaparece y aparece".
      window.addEventListener('message', function(ev) {
        try {
          var d = ev && ev.data;
          if (!d || d.type !== 'DAMYX_UPDATE_CODE') return;
          if (typeof d.code !== 'string') return;
          window.__damyx_current_code = d.code;
          renderComponent();
        } catch (e) { /* silent */ }
      });
    })();
  <\/script>
</body>
</html>`;
};

// =====================================================
// COMPONENTE PRINCIPAL: UnifiedCodePreview
// =====================================================

export const UnifiedCodePreview = ({
  code,
  title = '',
  autoRun = true,
  showWatermark = true,
  showControls = true,
  theme = 'dark',
  height = '400px',
  className = '',
  onError,
  onLoad
}) => {
  const iframeRef = useRef(null);
  const { language } = useLanguage();
  const es = language === 'es';
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // 🟥 FIX ESTRUCTURAL — eliminado el state `key` que forzaba remount del
  // iframe al togglear fullscreen. El iframe ahora vive UNA sola vez y
  // solo cambia su clase CSS según el modo.
  
  // Usamos srcDoc en vez de data URL: mas fiable, evita CSP/sandbox issues,
  // soporta codigo grande sin truncamiento y se carga sincronicamente.
  // 🟩 P26.22 · Hot-swap sin recargar iframe.
  //   - srcDoc: se calcula UNA sola vez al montar el iframe (initialContent).
  //   - Updates subsecuentes: se envían via postMessage al iframe cargado.
  //   Esto elimina el flash "desaparece y aparece" cuando el chat actualiza
  //   el código del proyecto en el Workspace.
  const [iframeReady, setIframeReady] = useState(false);
  const initialContentRef = useRef(null);
  // P26.29 · Modo HTML puro: el srcDoc se re-asigna en cada cambio (no hay
  // hot-swap por postMessage porque no hay componente React que remontar).
  const htmlMode = useMemo(() => isFullHtmlDocument(code), [code]);
  const iframeContent = useMemo(() => {
    if (!code) return '';
    try {
      return generatePreviewHTML(code, { showWatermark, theme });
    } catch (err) {
      setError(err.message);
      return '';
    }
  }, [code, showWatermark, theme]);
  // Solo setear el srcDoc la PRIMERA vez que tenemos content (en modo HTML
  // se actualiza siempre porque no existe hot-swap por postMessage).
  if (iframeContent && (!initialContentRef.current || htmlMode)) {
    initialContentRef.current = iframeContent;
  }

  // Cuando el code cambia y el iframe YA está listo, hot-swap via postMessage.
  const cleanedCodeMemo = useMemo(() => {
    if (!code) return '';
    if (isFullHtmlDocument(code)) return '';
    try { return cleanCodeForPreview(code); } catch { return ''; }
  }, [code]);
  const prevHtmlModeRef = useRef(htmlMode);
  useEffect(() => {
    const modeChanged = prevHtmlModeRef.current !== htmlMode;
    prevHtmlModeRef.current = htmlMode;
    if (htmlMode || modeChanged) {
      // HTML puro (o volvemos de HTML a React): re-inyectar el documento
      // completo, porque el iframe no tiene el listener de hot-swap.
      const ifr = iframeRef.current;
      if (ifr && iframeContent) {
        initialContentRef.current = iframeContent;
        ifr.srcdoc = iframeContent;
      }
      return;
    }
    if (!iframeReady) return;
    if (!cleanedCodeMemo) return;
    const iframeEl = iframeRef.current;
    if (!iframeEl || !iframeEl.contentWindow) return;
    try {
      iframeEl.contentWindow.postMessage({
        type: 'DAMYX_UPDATE_CODE',
        code: cleanedCodeMemo,
      }, '*');
    } catch { /* silent — cross-origin fallback: srcDoc se re-genera */ }
  }, [cleanedCodeMemo, iframeReady, htmlMode, iframeContent]);

  useEffect(() => {
    if (initialContentRef.current) {
      setIsLoading(true);
      setError(null);
    }
  }, []);

  // 🟥 CUADRO NEGRO 2026-07-03 — "MOSTRAR SIEMPRE": capturamos los errores
  // silenciosos que el iframe emite via postMessage. NUNCA se muestran
  // como ventanas rojas — solo un botón sutil bottom-right permite abrir
  // un panel opcional para users avanzados.
  const [silentErrors, setSilentErrors] = useState([]);
  const [errorPanelOpen, setErrorPanelOpen] = useState(false);
  useEffect(() => {
    const onMessage = (event) => {
      const data = event.data;
      if (!data || data.source !== 'damyxia-preview') return;
      if (data.type === 'damyxia:preview:error' && data.payload) {
        setSilentErrors((prev) => [...prev.slice(-19), data.payload]);
        // 🟥 CUADRO NEGRO 2026-07-03 — Telemetría silenciosa al backend.
        // Batcher en memoria (10s / 10 errores). Nunca rompe el UX.
        try {
          reportSilentError({
            type: data.payload.type || 'unknown',
            error: data.payload.message || data.payload.error || '',
            timestamp: data.payload.timestamp || Date.now(),
            component: 'UnifiedCodePreview',
            extra: data.payload.extra || undefined,
          });
        } catch (_) { /* silent */ }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  // Al cambiar el código, reseteamos el buffer.
  useEffect(() => {
    setSilentErrors([]);
    setErrorPanelOpen(false);
  }, [code]);

  // Fallback de seguridad: srcDoc es sincronico — el iframe se carga en
  // <300ms tipicamente. Si el evento `load` del iframe no dispara (sucede
  // cuando el componente se desmonta y vuelve a montar con el mismo
  // srcDoc, p.ej. al alternar fullscreen del panel padre), liberamos el
  // loader rapido (300ms en vez de 1500ms) para no congelar el spinner.
  useEffect(() => {
    if (!iframeContent) return;
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [iframeContent]);

  // Polling rapido del readyState. Si el iframe se remontó con cache
  // (toggle fullscreen → el evento `load` NO vuelve a dispararse), igual
  // detectamos que ya esta listo en <100ms y liberamos el loader.
  // Esto resuelve el bug: "tras agrandar/restaurar la preview, queda en
  // 'Iniciando workspace…' hasta hacer click manual en Refrescar".
  useEffect(() => {
    if (!iframeContent) return;
    let cancelled = false;
    let attempts = 0;
    const tick = () => {
      if (cancelled) return;
      const ifr = iframeRef.current;
      try {
        const doc = ifr?.contentDocument;
        if (doc && doc.readyState === 'complete') {
          setIsLoading(false);
          return;
        }
      } catch (e) { /* cross-origin: ignorar */ }
      attempts += 1;
      if (attempts < 10) setTimeout(tick, 50);
    };
    tick();
    return () => { cancelled = true; };
  }, [iframeContent]);
  
  const handleIframeLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };
  
  const handleRefresh = () => {
    // 🟥 FIX ESTRUCTURAL — el iframe NO debe cambiar su key. Para refrescar
    // re-asignamos el `srcdoc` vía DOM, lo que dispara `load` sin remount.
    const ifr = iframeRef.current;
    if (ifr && iframeContent) {
      setIsLoading(true);
      setError(null);
      ifr.srcdoc = iframeContent;
    }
  };

  // ============================================================
  // 🟥 P26.41 · RECOVERY DEL COMPILADOR (guardrail, regla 5 y 10)
  // "Compilando componente…" no puede quedar colgado: a los 15 s se corta
  // (stopCompile) y se recupera (openProject + showPreview). Máximo 2 ciclos;
  // si falla, se avisa SIN loops en vez de dejar el spinner eterno.
  // ============================================================
  const COMPILE_TIMEOUT_MS = 15000;
  const MAX_RECOVERY_CYCLES = 2;
  const [renderOk, setRenderOk] = useState(false);
  const [recoveryCycles, setRecoveryCycles] = useState(0);
  const [compileStalled, setCompileStalled] = useState(false);
  const recoveryRef = useRef(0);

  useEffect(() => {
    const onRendered = (event) => {
      const data = event.data;
      if (!data || data.source !== 'damyxia-preview') return;
      if (data.type === 'damyxia:preview:rendered' || data.type === 'damyxia:preview:error') {
        setRenderOk(true);
        setCompileStalled(false);
      }
    };
    window.addEventListener('message', onRendered);
    return () => window.removeEventListener('message', onRendered);
  }, []);

  // Reset del watchdog en cada cambio de código.
  useEffect(() => {
    setRenderOk(false);
    setCompileStalled(false);
    setRecoveryCycles(0);
    recoveryRef.current = 0;
  }, [code]);

  const stopCompile = useCallback(() => {
    const ifr = iframeRef.current;
    setIsLoading(false);
    try {
      ifr?.contentWindow?.postMessage({ type: 'DAMYX_STOP_COMPILE' }, '*');
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!iframeContent || renderOk || htmlMode) return undefined;
    const timer = setTimeout(() => {
      if (recoveryRef.current < MAX_RECOVERY_CYCLES) {
        recoveryRef.current += 1;
        setRecoveryCycles(recoveryRef.current);
        console.warn(`[P26.41] compilador colgado — recovery ciclo ${recoveryRef.current}`);
        stopCompile();
        handleRefresh();          // openProject + showPreview del mismo proyecto
      } else {
        stopCompile();
        setCompileStalled(true);  // se avisa y se corta: NO loops
      }
    }, COMPILE_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [iframeContent, renderOk, htmlMode, recoveryCycles, stopCompile]);

  // `stopCompile()` de la Workspace API llega por evento global.
  useEffect(() => {
    const onStop = () => { stopCompile(); setCompileStalled(true); };
    const onCompile = () => { recoveryRef.current = 0; setCompileStalled(false); handleRefresh(); };
    window.addEventListener('damyx:stop-compile', onStop);
    window.addEventListener('damyx:compile', onCompile);
    return () => {
      window.removeEventListener('damyx:stop-compile', onStop);
      window.removeEventListener('damyx:compile', onCompile);
    };
  });
  
  const toggleFullscreen = () => {
    // 🟥 FIX ESTRUCTURAL DEFINITIVO — al alternar fullscreen NO tocamos
    // el key del iframe. El iframe vive UNA sola vez en el árbol y solo
    // cambia su clase CSS (size). Eso elimina el bug "queda en blanco
    // hasta refrescar manualmente" tras agrandar/restaurar la preview.
    setIsFullscreen((cur) => !cur);
  };
  
  // Cuando se pide height="100%", NO podemos confiar en que la cadena de
  // flex/h-full ascendente realmente propague la altura. En navegadores
  // con `overflow-hidden` anidado el `100%` se colapsa a la altura del
  // contenido (ej: 400px del iframe default).
  //
  // Solucion: medir el contenedor padre en pixeles con ResizeObserver y
  // aplicar la altura medida. Garantiza que el iframe SIEMPRE ocupe el
  // espacio disponible real, sin importar la cadena flex.
  const containerRef = useRef(null);
  const [measuredHeight, setMeasuredHeight] = useState(null);

  useEffect(() => {
    if (height !== '100%' || isFullscreen) {
      setMeasuredHeight(null);
      return;
    }
    const el = containerRef.current?.parentElement;
    if (!el) return;
    const update = () => {
      const h = el.clientHeight;
      if (h > 0) setMeasuredHeight(h);
    };
    update();
    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    window.addEventListener('resize', update);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [height, isFullscreen]);

  if (!code) {
    return (
      <div className={`flex items-center justify-center bg-slate-800 rounded-lg border border-slate-700 ${className}`} style={{ height }}>
        <p className="text-slate-500 text-sm">{es ? 'Sin código para previsualizar' : 'No code to preview'}</p>
      </div>
    );
  }
  
  // Estrategia de altura:
  //   - fullscreen: container es `fixed inset-0` (toma viewport completo).
  //   - height === '100%': container es `position: absolute; inset: 0`
  //     dentro de un wrapper relative del consumidor. Esto ocupa el 100%
  //     real del padre sin depender de la cadena flex/min-h-0/overflow.
  //   - cualquier otro valor (ej '400px'): container con altura fija normal.
  const useAbsolute = !isFullscreen && height === '100%';

  const containerClasses = isFullscreen
    ? 'fixed inset-0 z-50 bg-slate-900'
    : useAbsolute
      ? `absolute inset-0 bg-slate-900 ${className}`
      : `relative rounded-lg overflow-hidden border border-slate-700 ${className}`;
  const containerStyle = isFullscreen
    ? { display: 'grid', gridTemplateRows: showControls ? 'auto 1fr' : '1fr' }
    : useAbsolute
      ? { display: 'grid', gridTemplateRows: showControls ? 'auto 1fr' : '1fr' }
      : { height, display: 'grid', gridTemplateRows: showControls ? 'auto 1fr' : '1fr' };

  return (
    <div ref={containerRef} className={containerClasses} style={containerStyle}>
      {/* Header con controles */}
      {showControls && (
        <div className="flex items-center justify-between px-3 py-2 bg-slate-800 border-b border-slate-700">
          <div className="flex items-center gap-2">
            <div className="flex gap-1">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              <div className="w-3 h-3 rounded-full bg-green-500" />
            </div>
            {title && <span className="text-xs text-slate-400 ml-2">{title}</span>}
          </div>
          
          <div className="flex items-center gap-1">
            <button
              onClick={handleRefresh}
              className="p-1.5 hover:bg-slate-700 rounded transition-colors"
              title={es ? 'Recargar' : 'Reload'}
            >
              <RefreshCw className="w-3.5 h-3.5 text-slate-400" />
            </button>
            <button
              onClick={toggleFullscreen}
              className="p-1.5 hover:bg-slate-700 rounded transition-colors"
              title={isFullscreen ? (es ? 'Salir de pantalla completa' : 'Exit fullscreen') : (es ? 'Pantalla completa' : 'Fullscreen')}
            >
              {isFullscreen ? (
                <Minimize2 className="w-3.5 h-3.5 text-slate-400" />
              ) : (
                <Maximize2 className="w-3.5 h-3.5 text-slate-400" />
              )}
            </button>
          </div>
        </div>
      )}
      
      {/* Área de preview — toma altura completa de la celda grid o flex */}
      <div className="relative w-full h-full bg-slate-900 min-h-0 overflow-hidden">
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-900/80 z-10">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              <span className="text-xs text-slate-400">Compilando...</span>
            </div>
          </div>
        )}
        
        {/* 🟥 P26.41 · Compilador colgado: aviso claro + reintento manual.
            Antes quedaba "Compilando componente…" para siempre. */}
        {compileStalled && (
          <div
            className="absolute inset-x-0 top-0 z-20 m-2 rounded-lg bg-amber-500/10 border border-amber-500/40 px-3 py-2 backdrop-blur"
            data-testid="preview-compile-stalled"
          >
            <p className="text-xs text-amber-200">
              {es
                ? `La compilación se cortó a los 15 s tras ${recoveryCycles} intento(s) de recuperación.`
                : `Compilation was stopped after 15 s and ${recoveryCycles} recovery attempt(s).`}
            </p>
            <button
              type="button"
              onClick={() => { recoveryRef.current = 0; setCompileStalled(false); handleRefresh(); }}
              data-testid="preview-retry-compile-btn"
              className="mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-400 hover:bg-amber-300 text-slate-950 text-xs font-semibold transition-colors"
            >
              <RefreshCw className="w-3 h-3" />
              {es ? 'Reintentar' : 'Retry'}
            </button>
          </div>
        )}

        {/* 🟥 CUADRO NEGRO 2026-07-03 — "MOSTRAR SIEMPRE":
            el banner rojo de "Error de compilación" fue eliminado.
            Los errores se capturan silenciosamente via silentErrors[] y
            se muestran solo si el user avanzado abre el panel opcional. */}

        {/* Panel opcional de errores — botón discreto bottom-right con contador */}
        {silentErrors.length > 0 && (
          <>
            <button
              type="button"
              onClick={() => setErrorPanelOpen((v) => !v)}
              className="absolute bottom-2 right-2 z-20 flex items-center gap-1 px-2 py-1 rounded-md bg-slate-800/80 hover:bg-slate-700/90 backdrop-blur border border-slate-600/50 text-[10px] text-slate-300 font-mono transition-colors"
              data-testid="silent-errors-toggle"
              title={es
                ? `${silentErrors.length} detalle(s) técnico(s) capturado(s). Click para ver.`
                : `${silentErrors.length} technical detail(s) captured. Click to view.`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
              {silentErrors.length}
            </button>
            {errorPanelOpen && (
              <div
                className="absolute bottom-10 right-2 z-30 w-80 max-h-64 overflow-auto bg-slate-900/95 backdrop-blur border border-slate-700 rounded-lg p-3 shadow-xl"
                data-testid="silent-errors-panel"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold text-slate-300">
                    {es ? 'Detalles técnicos' : 'Technical details'}
                  </span>
                  <button
                    type="button"
                    onClick={() => setErrorPanelOpen(false)}
                    className="text-slate-500 hover:text-slate-300 text-xs"
                    aria-label="close"
                  >
                    ×
                  </button>
                </div>
                <ul className="space-y-1.5">
                  {silentErrors.map((e, i) => (
                    <li key={i} className="text-[10px] font-mono text-slate-400 border-l-2 border-slate-700 pl-2 py-1">
                      <div className="text-slate-500 uppercase text-[9px] tracking-wider">
                        {e.type || 'unknown'}
                      </div>
                      <div className="text-slate-300 break-all whitespace-pre-wrap">
                        {e.message || '—'}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
        
        <iframe
          ref={iframeRef}
          srcDoc={initialContentRef.current || ''}
          onLoad={(e) => {
            setIframeReady(true);
            if (typeof handleIframeLoad === 'function') handleIframeLoad(e);
          }}
          onError={() => {
            // 🟥 CUADRO NEGRO — silencioso, sin banner rojo.
            setSilentErrors((prev) => [...prev.slice(-19), {
              type: 'iframe_load',
              message: 'Error al cargar el preview',
              timestamp: Date.now(),
            }]);
            setIsLoading(false);
          }}
          className="block border-0"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            background: theme === 'dark' ? '#0f172a' : '#ffffff'
          }}
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
          title="Code Preview"
        />
      </div>
    </div>
  );
};

// =====================================================
// VARIANTES ESPECIALIZADAS
// =====================================================

/**
 * Preview simple sin controles (para galerías y thumbnails)
 */
export const SimplePreview = ({ code, height = '200px', className = '' }) => (
  <UnifiedCodePreview
    code={code}
    height={height}
    showControls={false}
    showWatermark={false}
    className={className}
  />
);

/**
 * Preview compacto para mensajes de chat
 */
export const ChatPreview = ({ code, title }) => (
  <UnifiedCodePreview
    code={code}
    title={title || 'Vista previa'}
    height="350px"
    showWatermark={true}
    showControls={true}
  />
);

/**
 * Preview de pantalla completa para workspace
 */
export const FullPreview = ({ code, title, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
    <div className="w-full max-w-6xl h-[90vh] bg-slate-900 rounded-xl overflow-hidden shadow-2xl">
      <UnifiedCodePreview
        code={code}
        title={title}
        height="100%"
        showWatermark={true}
        showControls={true}
      />
    </div>
  </div>
);

// Export default
export default UnifiedCodePreview;
